import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  ArrowRight,
  CalendarClock,
  CalendarDays,
  ChevronRight,
  Clock,
  History,
  RefreshCw,
  Smartphone,
  UserPlus,
  Users,
  Zap,
} from "lucide-react";
import betaAPI from "v2/api";
import { dateLocale } from "i18n";
import TeamLoadChart from "./TeamLoadChart";
import { formatRelativeDate } from "./TeamSessionsSection";
import { sessionsPerWeek, summariseSessions } from "./overviewSessions";

// Enough to find the next and last sessions and fill eight weeks of the
// weekly chart for a typical team.
const SESSIONS_PAGE_SIZE = 50;
const RECENT_COUNT = 8;
const AVATAR_COUNT = 5;
const WEEKS = 8;

const TYPE_STYLES = {
  training: { bg: "#FEF3C7", color: "#B45309" },
  match: { bg: "#E0E7FF", color: "#4F46E5" },
  recovery: { bg: "#CCFBF1", color: "#0F766E" },
};

const initials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

const parseSessionDate = (s) => {
  if (s.start_time) return new Date(s.start_time);
  if (s.date) {
    const [y, m, d] = s.date.split("-").map(Number);
    return new Date(y, m - 1, d);
  }
  return s.created_at ? new Date(s.created_at) : null;
};

const formatSessionDate = (s) => {
  const date = parseSessionDate(s);
  if (!date) return "";
  const day = date.toLocaleDateString(dateLocale("en-GB"), {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
  if (!s.start_time) return day;
  const time = date.toLocaleTimeString(dateLocale("en-GB"), {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${day} · ${time}`;
};

const sessionPath = (workspaceId, teamId, sessionId) =>
  `/v2/${workspaceId}/team/${teamId}/session/${sessionId}`;

// ── Building blocks ─────────────────────────────────────────────────────────

const Card = ({ children, className = "", ...rest }) => (
  <section
    className={`flex flex-col rounded-xl border border-[#F3F4F6] bg-white ${className}`}
    {...rest}
  >
    {children}
  </section>
);

const CardHeader = ({ icon: Icon, title, aside }) => (
  <div className="flex items-center justify-between gap-3 px-5 pt-4">
    <div className="flex items-center gap-2">
      <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#F3F4F6]">
        <Icon size={14} className="text-[#6B7280]" />
      </div>
      <h3 className="text-sm font-semibold text-[#111827]">{title}</h3>
    </div>
    {aside}
  </div>
);

const CardLink = ({ onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className="group mt-auto flex min-h-[44px] w-full items-center justify-between border-t border-[#F3F4F6] px-5 py-3 text-left text-[13px] font-medium text-[#E07A3A] transition duration-150 hover:bg-[#FEF7F3]"
  >
    {children}
    <ArrowRight
      size={14}
      className="transition-transform duration-150 group-hover:translate-x-0.5"
    />
  </button>
);

const TypeBadge = ({ type }) => {
  const { t } = useTranslation();
  if (!type) return null;
  const style = TYPE_STYLES[type] || { bg: "#F3F4F6", color: "#6B7280" };
  return (
    <span
      className="inline-flex flex-shrink-0 rounded-md px-2 py-0.5 text-[11px] font-medium"
      style={{ backgroundColor: style.bg, color: style.color }}
    >
      {TYPE_STYLES[type] ? t(`team.sessionTypes.${type}`) : type}
    </span>
  );
};

const Stat = ({ value, label }) => (
  <div className="min-w-0">
    <p className="text-xl font-bold text-[#111827]">{value}</p>
    <p className="text-xs text-[#6B7280]">{label}</p>
  </div>
);

const EmptyNote = ({ title, body }) => (
  <div className="px-5 pb-5 pt-4">
    <p className="text-[13px] font-medium text-[#374151]">{title}</p>
    <p className="mt-0.5 text-xs leading-relaxed text-[#6B7280]">{body}</p>
  </div>
);

const CardSkeleton = () => (
  <div className="animate-pulse px-5 pb-5 pt-4">
    <div className="mb-2 h-4 w-40 rounded bg-[#E5E7EB]" />
    <div className="mb-4 h-3 w-24 rounded bg-[#E5E7EB]" />
    <div className="h-8 w-full rounded bg-[#F3F4F6]" />
  </div>
);

// ── Cards ───────────────────────────────────────────────────────────────────

const AthletesCard = ({
  players,
  totalCount,
  under16,
  onViewAthletes,
  onAddPlayer,
  onViewConsent,
}) => {
  const { t } = useTranslation();
  const shown = players.slice(0, AVATAR_COUNT);
  const extra = Math.max(totalCount - shown.length, 0);
  const awaiting = under16
    ? players.filter((p) => p.consentStatus === "awaiting").length
    : 0;

  return (
    <Card>
      <CardHeader icon={Users} title={t("team.overviewTab.athletes.title")} />
      {totalCount === 0 ? (
        <>
          <EmptyNote
            title={t("team.overviewTab.athletes.emptyTitle")}
            body={t("team.overviewTab.athletes.emptyBody")}
          />
          <CardLink onClick={onAddPlayer}>
            <span className="flex items-center gap-1.5">
              <UserPlus size={14} />
              {t("team.overviewTab.athletes.add")}
            </span>
          </CardLink>
        </>
      ) : (
        <>
          <div className="px-5 pb-5 pt-4">
            <p className="text-[28px] font-bold leading-none text-[#111827]">
              {totalCount}
            </p>
            <p className="mt-1 text-xs text-[#6B7280]">
              {t("team.overviewTab.athletes.count", { count: totalCount })}
            </p>
            <div className="mt-4 flex items-center">
              <div className="flex -space-x-2">
                {shown.map((p) => (
                  <span
                    key={p.id}
                    title={p.name}
                    className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-[#FEF3E7] text-[11px] font-semibold text-[#C2410C]"
                  >
                    {initials(p.name)}
                  </span>
                ))}
                {extra > 0 && (
                  <span className="flex h-8 min-w-[32px] items-center justify-center rounded-full border-2 border-white bg-[#F3F4F6] px-1.5 text-[11px] font-semibold text-[#6B7280]">
                    {t("team.overviewTab.athletes.more", { value: extra })}
                  </span>
                )}
              </div>
              <p className="ml-3 min-w-0 truncate text-xs text-[#6B7280]">
                {shown
                  .slice(0, 2)
                  .map((p) => p.name.split(" ")[0])
                  .join(", ")}
              </p>
            </div>
            {awaiting > 0 && (
              <button
                type="button"
                onClick={onViewConsent}
                className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-[#FFFBEB] px-2.5 py-1 text-xs font-medium text-[#92400E] transition duration-150 hover:bg-[#FEF3C7]"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-[#F59E0B]" />
                {t("team.overviewTab.athletes.awaitingConsent", {
                  count: awaiting,
                })}
              </button>
            )}
          </div>
          <CardLink onClick={onViewAthletes}>
            {t("team.overviewTab.athletes.view")}
          </CardLink>
        </>
      )}
    </Card>
  );
};

const NextSessionCard = ({ session, loading, onOpen }) => {
  const { t } = useTranslation();
  const live = session?.status === "active";
  return (
    <Card>
      <CardHeader
        icon={CalendarClock}
        title={t("team.overviewTab.nextSession.title")}
        aside={
          live && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#ECFDF5] px-2 py-0.5 text-[11px] font-semibold text-[#047857]">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#10B981]" />
              {t("team.overviewTab.nextSession.live")}
            </span>
          )
        }
      />
      {loading ? (
        <CardSkeleton />
      ) : !session ? (
        <EmptyNote
          title={t("team.overviewTab.nextSession.emptyTitle")}
          body={t("team.overviewTab.nextSession.emptyBody")}
        />
      ) : (
        <>
          <div className="px-5 pb-5 pt-4">
            <div className="flex items-start justify-between gap-3">
              <p className="min-w-0 text-[15px] font-semibold text-[#111827]">
                {session.name || t("team.overview.fallbackSessionName")}
              </p>
              <TypeBadge type={session.type} />
            </div>
            <p className="mt-1 text-[13px] text-[#6B7280]">
              {formatSessionDate(session)}
            </p>
          </div>
          <CardLink onClick={() => onOpen(session)}>
            {t("team.overviewTab.viewSession")}
          </CardLink>
        </>
      )}
    </Card>
  );
};

const LastSessionCard = ({ session, loading, onOpen }) => {
  const { t } = useTranslation();
  return (
    <Card className="h-full">
      <CardHeader
        icon={History}
        title={t("team.overviewTab.lastSession.title")}
      />
      {loading ? (
        <CardSkeleton />
      ) : !session ? (
        <EmptyNote
          title={t("team.overviewTab.lastSession.emptyTitle")}
          body={t("team.overviewTab.lastSession.emptyBody")}
        />
      ) : (
        <>
          <div className="px-5 pb-5 pt-4">
            <div className="flex items-start justify-between gap-3">
              <p className="min-w-0 text-[15px] font-semibold text-[#111827]">
                {session.name || t("team.overview.fallbackSessionName")}
              </p>
              <TypeBadge type={session.type} />
            </div>
            <p className="mt-1 text-[13px] text-[#6B7280]">
              {formatSessionDate(session)}
            </p>
            <div className="mt-4 grid grid-cols-3 gap-3">
              <Stat
                value={session.assignment_count ?? "–"}
                label={t("team.overviewTab.lastSession.athletes")}
              />
              <Stat
                value={session.total_impacts ?? "–"}
                label={t("team.overviewTab.lastSession.impacts")}
              />
              <Stat
                value={
                  session.peak_g != null
                    ? t("team.overviewTab.lastSession.peakValue", {
                        value: Math.round(session.peak_g),
                      })
                    : "–"
                }
                label={t("team.overviewTab.lastSession.peak")}
              />
            </div>
          </div>
          <CardLink onClick={() => onOpen(session)}>
            {t("team.overviewTab.viewSession")}
          </CardLink>
        </>
      )}
    </Card>
  );
};

const RecentSessionsCard = ({ sessions, totalCount, onOpen, onSeeAll }) => {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader
        icon={CalendarDays}
        title={t("team.overviewTab.sessions.title")}
        aside={
          <span className="rounded-full bg-[#F3F4F6] px-2 py-0.5 text-xs font-medium text-[#6B7280]">
            {totalCount}
          </span>
        }
      />
      <p className="px-5 pt-1 text-xs text-[#6B7280]">
        {t("team.overviewTab.sessions.total", { count: totalCount })}
      </p>
      <ul className="mt-2">
        {sessions.map((s) => {
          const isMatch = s.type === "match";
          const Icon = isMatch ? Zap : Clock;
          return (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => onOpen(s)}
                className="flex min-h-[44px] w-full items-center gap-3 px-5 py-2.5 text-left transition duration-150 hover:bg-[#FAFAFA]"
              >
                <span
                  className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg ${
                    isMatch ? "bg-[#FEF3E7]" : "bg-[#F3F4F6]"
                  }`}
                >
                  <Icon
                    size={14}
                    className={isMatch ? "text-[#E07A3A]" : "text-[#6B7280]"}
                  />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-medium text-[#111827]">
                    {s.name || t("team.overview.fallbackSessionName")}
                  </span>
                  <span className="block text-[11px] text-[#9CA3AF]">
                    {formatRelativeDate(s.start_time || s.date || s.created_at)}
                    {s.total_impacts != null && (
                      <>
                        {" · "}
                        {t("team.overviewTab.sessions.impacts", {
                          count: s.total_impacts,
                        })}
                      </>
                    )}
                  </span>
                </span>
                <ChevronRight
                  size={14}
                  className="flex-shrink-0 text-[#D1D5DB]"
                />
              </button>
            </li>
          );
        })}
      </ul>
      <CardLink onClick={onSeeAll}>
        {t("team.overviewTab.sessions.seeAll")}
      </CardLink>
    </Card>
  );
};

const WeeklyTooltip = ({ active, payload }) => {
  const { t } = useTranslation();
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  return (
    <div className="min-w-[150px] rounded-lg border border-[#F3F4F6] bg-white px-3 py-2.5 shadow-lg">
      <p className="mb-1.5 text-[11px] text-[#9CA3AF]">
        {t("team.overviewTab.weekly.weekOf", { date: point.label })}
      </p>
      <p className="text-[13px] font-semibold text-[#111827]">
        {t("team.overviewTab.weekly.sessions", { count: point.total })}
      </p>
      {point.total > 0 && (
        <p className="mt-0.5 text-[11px] text-[#6B7280]">
          {t("team.overviewTab.weekly.breakdown", {
            training: point.training,
            match: point.match,
          })}
        </p>
      )}
    </div>
  );
};

const WeeklySessionsCard = ({ sessions }) => {
  const { t } = useTranslation();
  const data = useMemo(
    () =>
      sessionsPerWeek(sessions, WEEKS).map((w) => ({
        ...w,
        label: w.weekStart.toLocaleDateString(dateLocale("en-GB"), {
          day: "numeric",
          month: "short",
        }),
      })),
    [sessions]
  );
  const hasOther = data.some((w) => w.other > 0);

  return (
    <Card className="p-5">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-[#111827]">
            {t("team.overviewTab.weekly.title")}
          </h3>
          <p className="text-xs text-[#6B7280]">
            {t("team.overviewTab.weekly.subtitle", { value: WEEKS })}
          </p>
        </div>
        <div className="flex items-center gap-4 text-xs text-[#6B7280]">
          <span className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded bg-[#B45309]" />
            {t("team.sessionTypes.training")}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded bg-[#4F46E5]" />
            {t("team.sessionTypes.match")}
          </span>
        </div>
      </div>
      <div className="h-[200px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 4, right: 4, left: -24 }}>
            <CartesianGrid vertical={false} stroke="#F3F4F6" />
            <XAxis
              dataKey="label"
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 11, fill: "#9CA3AF" }}
              interval="preserveStartEnd"
            />
            <YAxis
              allowDecimals={false}
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 11, fill: "#9CA3AF" }}
            />
            <Tooltip content={<WeeklyTooltip />} cursor={{ fill: "#FAFAFA" }} />
            <Bar
              dataKey="training"
              stackId="s"
              fill="#B45309"
              maxBarSize={36}
            />
            <Bar dataKey="match" stackId="s" fill="#4F46E5" maxBarSize={36} />
            {hasOther && (
              <Bar dataKey="other" stackId="s" fill="#D1D5DB" maxBarSize={36} />
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
};

const NoSessionsCard = () => {
  const { t } = useTranslation();
  return (
    <Card className="items-center px-6 py-12 text-center">
      <div className="relative mb-4">
        <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-[#FEF3E7]">
          <CalendarDays size={26} className="text-[#E07A3A]" />
        </div>
        <div className="absolute -bottom-1 -right-2 flex h-6 w-6 items-center justify-center rounded-lg border border-[#F3F4F6] bg-white shadow-sm">
          <Smartphone size={12} className="text-[#9CA3AF]" />
        </div>
      </div>
      <h3 className="text-[15px] font-semibold text-[#111827]">
        {t("team.overviewTab.empty.title")}
      </h3>
      <p className="mt-1 text-sm text-[#374151]">
        {t("team.overviewTab.empty.cta")}
      </p>
      <p className="mt-2 max-w-[420px] text-xs leading-relaxed text-[#6B7280]">
        {t("team.overviewTab.empty.body")}
      </p>
    </Card>
  );
};

// ── Tab ─────────────────────────────────────────────────────────────────────

/**
 * The first thing a coach sees on a team: athletes, the next and last
 * session, recent sessions and a couple of calm charts. Everything links on
 * to the detailed tabs or the session page.
 */
const OverviewTab = ({
  workspaceId,
  teamId,
  team,
  players = [],
  playersTotalCount = 0,
  teamLoadData = [],
  onOpenTab,
  onAddPlayer,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [state, setState] = useState({
    loading: true,
    error: false,
    sessions: [],
    totalCount: 0,
  });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: false }));
    try {
      const res = await betaAPI.getTeamSessions({
        workspaceId,
        teamId,
        queryParams: {
          page: 1,
          page_size: SESSIONS_PAGE_SIZE,
          ordering: "recent",
        },
      });
      const payload = res.data?.sessions;
      // Older API builds return a flat array of every session.
      const sessions = Array.isArray(payload)
        ? payload
        : payload?.results || [];
      const totalCount = Array.isArray(payload)
        ? payload.length
        : payload?.total_count ?? sessions.length;
      setState({ loading: false, error: false, sessions, totalCount });
    } catch (err) {
      console.error("Failed to load team sessions", err);
      setState({ loading: false, error: true, sessions: [], totalCount: 0 });
    }
  }, [workspaceId, teamId]);

  useEffect(() => {
    load();
  }, [load]);

  const { next, recorded } = useMemo(
    () => summariseSessions(state.sessions),
    [state.sessions]
  );

  // Upcoming sessions aren't "recorded", so leave them out of the count.
  const recordedCount = Math.max(
    0,
    state.totalCount - (state.sessions.length - recorded.length)
  );

  const openSession = (s) => navigate(sessionPath(workspaceId, teamId, s.id));
  const noSessions = !state.loading && !state.error && state.totalCount === 0;

  return (
    <div className="mx-auto min-h-0 w-full max-w-[1440px] flex-1 overflow-y-auto px-4 pb-8 pt-5 sm:px-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        <AthletesCard
          players={players}
          totalCount={playersTotalCount}
          under16={!!team?.under16}
          onViewAthletes={() => onOpenTab("impact-load")}
          onAddPlayer={onAddPlayer}
          onViewConsent={() => onOpenTab("consent")}
        />
        <NextSessionCard
          session={next}
          loading={state.loading}
          onOpen={openSession}
        />
        <div className="md:col-span-2 xl:col-span-1">
          <LastSessionCard
            session={recorded[0]}
            loading={state.loading}
            onOpen={openSession}
          />
        </div>
      </div>

      <div className="mt-4">
        {state.error ? (
          <Card className="items-center px-6 py-10 text-center">
            <p className="text-sm font-semibold text-[#111827]">
              {t("team.overviewTab.loadFailed")}
            </p>
            <button
              type="button"
              onClick={load}
              className="mt-3 flex min-h-[36px] items-center gap-1.5 rounded-lg border border-[#E5E7EB] px-3 py-1.5 text-xs font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB]"
            >
              <RefreshCw size={12} />
              {t("common.actions.retry")}
            </button>
          </Card>
        ) : state.loading ? (
          <Card className="h-[280px] animate-pulse">
            <div className="m-5 h-full rounded-lg bg-[#F3F4F6]" />
          </Card>
        ) : noSessions ? (
          <NoSessionsCard />
        ) : (
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            <div className="flex min-w-0 flex-col gap-4 xl:col-span-2">
              <WeeklySessionsCard sessions={recorded} />
              <Card className="p-5">
                <TeamLoadChart data={teamLoadData} dataKey="teamLoad" />
              </Card>
            </div>
            <RecentSessionsCard
              sessions={recorded.slice(0, RECENT_COUNT)}
              totalCount={recordedCount}
              onOpen={openSession}
              onSeeAll={() => onOpenTab("impact-load")}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default OverviewTab;
